import { useState, useEffect } from "react";
import { useLocalization, Localized } from "@fluent/react";
import { Button } from "@narmi/design_system";
import { Dialog, useLoadingContext } from "cerulean";
import {
  useInstitutionSettings,
  InstitutionSettingsContextProvider,
} from "./contexts/InstitutionSettingsContext";
import styles from "./OAuth2Authorize.module.scss";
import { TermsOfUseDialog } from "./settings/business_banking_api/modals";

const onUserDismiss = () => {
  document.getElementById("cancel-button")?.click();
};

const OAuth2AuthorizeComponent = ({
  name,
  scopes,
}: {
  name: string;
  scopes: string[];
}) => {
  const { l10n } = useLocalization();
  const { setIsLoading } = useLoadingContext();
  const institutionSettings = useInstitutionSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    if (institutionSettings.long_name) {
      setIsLoading(false);
    }
  }, [institutionSettings.long_name]);

  const handleAuthorizeClick = async () => {
    setIsSubmitting(true);
    document.getElementById("authorize-button")?.click();
  };

  const institutionName = institutionSettings.long_name;
  return (
    <Dialog
      isOpen={true}
      onUserDismiss={onUserDismiss}
      title={l10n.getString("dialog-title-authorize-oauth2", { appName: name })}
      footer={
        <div className={styles.authorizeButtons}>
          <Button
            kind="negative"
            onClick={onUserDismiss}
            disabled={isSubmitting}
            label={l10n.getString("button-cancel")}
          />
          <div style={{ width: "12px" }} />
          <Button
            onClick={handleAuthorizeClick}
            isLoading={isSubmitting}
            label={l10n.getString("button-authorize")}
          />
        </div>
      }
    >
      <div className={styles.authorizeAppHeader}>
        <div className={styles.genericBankLogo}>
          <span className={`narmi-icon-bank ${styles.icon}`} />
        </div>
        <Localized
          id="oauth2-app-wants-access"
          vars={{ name, institutionName }}
        >
          <div className={styles.appAccountAccess}>
            {name} needs permission to access your{" "}
            <strong>{institutionName}</strong> account(s).
          </div>
        </Localized>
      </div>
      <div className={styles.appPermissions}>
        {l10n.getString("oauth2-app-permissions-required")}
        <ul>
          {scopes.map((scope) => (
            <li key={scope}>{scope}</li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
};

const OAuth2Authorize = ({
  name,
  isDcr,
  scopes,
}: {
  name: string;
  isDcr: any;
  scopes: string;
}) => {
  const [showDcrConfirmation, setShowDcrConfirmation] = useState(
    JSON.parse(isDcr) as boolean // should be 'true' => true
  );

  return (
    <InstitutionSettingsContextProvider>
      {showDcrConfirmation ? (
        <TermsOfUseDialog
          isOpen={showDcrConfirmation}
          onUserDismiss={onUserDismiss}
          onConfirm={() => setShowDcrConfirmation(false)}
        />
      ) : (
        <OAuth2AuthorizeComponent name={name} scopes={JSON.parse(scopes)} />
      )}
    </InstitutionSettingsContextProvider>
  );
};

export default OAuth2Authorize;
